import { useEffect, useState, useMemo, memo } from 'react';
import {
  Box, Tabs, Tab, Chip, Stack, Alert, Button, Typography, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel, Switch, Radio, TextField, LinearProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  setRateCardRows, markRateCardCodes, setSlabTemplate, setActiveLane,
  toggleSortByStatus, updateRateCardRow, recordConfirmation, clearConfirmation,
} from '../../../../redux/pricingWizardSlice';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';
import {
  buildRateCardRows, slabsForTemplate, SLAB_TEMPLATES, summarizeSlabGroups,
  classifyServiceLane, BLENDED_LANES, foldIndependentRvpIntoRows,
} from '../helpers/rateEngine';
import { ARRANGEMENTS } from '../constants';
import CellInput from '../components/CellInput';
import BasePricingUpload from '../components/BasePricingUpload';

const LANE_META = Object.fromEntries(BLENDED_LANES.map((l) => [l.id, l]));
const laneLabel = (id) => LANE_META[id]?.label || 'Surface';
const laneIconLabel = (id) => `${LANE_META[id]?.icon || '🚛'} ${LANE_META[id]?.label || 'Surface'}`;

// ── Slab summary chip ───────────────────────────────────────────────────────
function SlabSummary({ wizard }) {
  const slabs = slabsForTemplate(wizard);
  const tpl = SLAB_TEMPLATES.find((t) => t.id === wizard.rateCard.slabTemplate);
  const groups = summarizeSlabGroups(slabs);
  const maxG = slabs[slabs.length - 1][1];
  const fmtKg = (g) => (g >= 1000 ? `${(g / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg` : `${g} GMS`);
  return (
    <Paper variant="outlined" sx={{ mt: 1.25, p: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderColor: 'primary.light' }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>⚖ Slab template</Typography>
      <Chip size="small" label={tpl?.label} sx={{ bgcolor: 'background.neutral', fontWeight: 600 }} />
      {groups.map((g, i) => (
        <Chip key={i} size="small" label={`${g.count} × ${g.width} GMS · ${fmtKg(g.min)}→${fmtKg(g.max)}`}
          sx={{ bgcolor: 'primary.lighter', color: 'primary.dark', fontWeight: 700, fontSize: 10.5 }} />
      ))}
      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>{slabs.length} slabs · max {fmtKg(maxG)}</Typography>
    </Paper>
  );
}

// ── Editable 17-column matrix for one account code ──────────────────────────
const COLS = [
  ['zone', 'Zone'], ['start', 'Start'], ['min', 'Min'], ['max', 'Max'], ['price', 'Price ₹'],
  ['addl_unit', '+Unit'], ['addl_rate', '+Rate'], ['vc', 'VC'], ['tax', 'Tax%'], ['fsc', 'FSC%'],
  ['docket', 'Docket'], ['cod_pct', 'COD%'], ['cod_op', 'Op'], ['cod_fixed', 'COD₹'],
  ['rvp_noqc', 'RVP nQC'], ['rvp_qc', 'RVP QC'], ['rto_pct', 'RTO%'],
];

const ZONE_LABELS = ['Zone 1 · Local', 'Zone 2 · Regional', 'Zone 3 · Metro', 'Zone 4 · ROI', 'Zone 5 · Special'];

// Memoised so parent re-renders (e.g. completion map changes) don't re-paint 60 inputs.
const RateMatrix = memo(function RateMatrix({ rows, accountCode, arrangementType, onCell }) {
  const [activeZone, setActiveZone] = useState(1);

  if (arrangementType === 'ZAWA') {
    const r0 = rows[0];
    return (
      <Alert severity="info" icon={<span>📌</span>} sx={{ mt: 1 }}>
        Flat rate <b>₹{r0?.price}</b> applies to every zone (1–5). VC {r0?.vc} · RTO {r0?.rto_pct}% of forward. Shipments above ceiling are unserviceable.
      </Alert>
    );
  }

  const editable = (r, key) => (
    <CellInput
      type={key === 'cod_op' ? 'text' : 'number'}
      value={r[key]}
      width={key === 'cod_op' ? 44 : 46}
      onCommit={(val) => onCell(r, key, key === 'cod_op' ? val : (Number(val) || 0))}
    />
  );

  // Show one zone at a time — reduces rendered inputs from (slabs×5×16) to (slabs×16).
  const zoneRows = rows.filter((r) => r.zone === activeZone);
  const zones = [...new Set(rows.map((r) => r.zone))].sort();

  return (
    <Box sx={{ mt: 1 }}>
      <Tabs
        value={activeZone}
        onChange={(_, v) => setActiveZone(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 11.5, fontWeight: 600, textTransform: 'none', py: 0.5 } }}
      >
        {zones.map((z) => (
          <Tab key={z} value={z} label={ZONE_LABELS[z - 1] || `Zone ${z}`} />
        ))}
      </Tabs>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: 'background.neutral', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', p: 0.75 } }}>
              {COLS.map(([k, label]) => <TableCell key={k} align={k === 'zone' ? 'left' : 'right'}>{label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {zoneRows.map((r, i) => (
              // Include accountCode in the key: CellInput is uncontrolled (defaultValue
              // read once on mount), so without this React reuses the same input nodes
              // across courier/lane switches and shows stale prices. Keying per account
              // forces a remount so each lane's values actually appear.
              <TableRow key={`${accountCode}-${r.zone}-${r.start}-${i}`}>
                {COLS.map(([k]) => (
                  <TableCell key={k} align={k === 'zone' ? 'left' : 'right'} sx={{ p: 0.4 }}>
                    {k === 'zone' ? <b>{r.zone}</b> : editable(r, k)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
});

export default function Stage4_RateCard() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const wizard = useSelector((s) => s.pricingWizard);
  const { rateCard, rateCardMode, rvp, arrangement, agreement } = wizard;
  const isBlendedMode = rateCardMode === 'blended';

  const [activeParent, setActiveParent] = useState(null);
  const [activeAcc, setActiveAcc] = useState(null);
  const [slabPickerOpen, setSlabPickerOpen] = useState(false);
  const [slabSel, setSlabSel] = useState(rateCard.slabTemplate);
  const [customSlabs, setCustomSlabs] = useState(rateCard.customSlabs);

  // Rebuild rows when:
  //  • rows are empty (first entry or after template change clears them)
  //  • agreement.confirmed flips to true (analyst just confirmed Preview & Confirm)
  //    — this forces fresh rows with all 7 confirmed couriers and their actual prices
  // EXCEPT when the rows came from a template upload: the uploaded file is the
  // source of truth (per-service prices, file-level T&C) and the engine rebuild
  // cannot reproduce it. Changing the slab template or weight ceiling clears the
  // rows (and the flag), which re-enables the rebuild. Known tradeoff: re-running
  // AI extraction does NOT refresh template-uploaded rows — re-upload or change
  // the slab template to discard them.
  const { confirmed: agreementConfirmed } = agreement;
  useEffect(() => {
    // PREVIEW = WHAT WAS UPLOADED. rateCard.rows is the single source of truth (it is
    // always faithful per-account in both blended and courier-wise — verified). The
    // engine (buildRateCardRows) re-derives from parent-keyed extracted rates and
    // collapses lanes when couriers share a parent, so it must NEVER overwrite existing
    // rows. It only SEEDS an empty card (fresh setup with no upload). A slab-template
    // change clears rows (setSlabTemplate), so that path still rebuilds.
    if (rateCard.rows.length) return;
    dispatch(setRateCardRows(buildRateCardRows(wizard)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementConfirmed, rateCard.slabTemplate]);

  // RVP Mode A (forward-linked): reverse services are priced on their OWN reverse
  // couriers (RVP derived from the forward sibling). If the card was built before Mode A
  // was selected, those reverse rows are missing/stale. Merge them in WITHOUT touching
  // the forward rows (preserves uploaded values + manual edits). Never runs for template
  // uploads (the file is the source of truth) or once correct reverse rows already exist.
  const rvpModeA = rvp.enabled && rvp.mode === 'forward_linked';
  useEffect(() => {
    if (!rvpModeA || !rateCard.rows.length || rateCard.rowsSource === 'template') return;
    const revSvcIds = new Set(
      (wizard.resolvedServices || [])
        .filter((s) => s.family === 'Reverse' || (s.id && s.id.startsWith('REV_')))
        .map((s) => s.id),
    );
    if (!revSvcIds.size) return;
    const built = buildRateCardRows(wizard);
    const builtRev = built.filter((r) => revSvcIds.has(r.service));
    const revCodes = new Set(builtRev.map((r) => r.account_code));
    if (!revCodes.size) return;
    // Already have the correct per-reverse-courier rows → nothing to do.
    const haveCorrect = rateCard.rows.some((r) => revSvcIds.has(r.service) && revCodes.has(r.account_code));
    if (haveCorrect) return;
    // Keep every non-reverse row (forward + edits); replace reverse rows with the correct ones.
    const merged = [...rateCard.rows.filter((r) => !revSvcIds.has(r.service)), ...builtRev];
    dispatch(setRateCardRows(merged));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rvpModeA, agreementConfirmed, rateCard.slabTemplate, rateCard.rows.length]);

  const { data: groups = [] } = useGetServiceabilityGroupsQuery();

  // Returns true when a courier parent has NO matching entry in serviceabilityGroups.
  // Uses the same partial case-insensitive match as buildRateCardRows so the result
  // is always consistent with why a courier got a placeholder account code.
  const isNotInServiceability = (parentName) => {
    const needle = parentName.toLowerCase();
    return !groups.some((g) =>
      (g.linkedCodes || []).some((lc) => {
        const p = (lc.parent || lc.cpAccountCode || '').toLowerCase();
        return p.includes(needle) || needle.includes(p);
      })
    );
  };

  // Mode A: overlay the live RVP formula so the preview's RVP nQC/QC always reflect the
  // current formula (the values baked into the rows at upload can be stale). No-op for
  // Mode B / RVP-off. Publish runs the same fold, so preview and published values match.
  const rows = useMemo(
    () => foldIndependentRvpIntoRows(rateCard.rows, wizard),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rateCard.rows, rvp, rateCardMode, agreement],
  );
  const accsRaw = [...new Set(rows.map((r) => r.account_code))];
  const parentsRaw = [...new Set(rows.map((r) => r.parent))];

  const codesUnderParent = (par) => [...new Set(rows.filter((r) => r.parent === par).map((r) => r.account_code))];
  const laneOfAcc = (acc) => { const r = rows.find((x) => x.account_code === acc); return r ? classifyServiceLane(r.service) : 'surface'; };
  const codesInLane = (lane) => accsRaw.filter((a) => laneOfAcc(a) === lane);
  const parentStatus = (par) => {
    const codes = codesUnderParent(par);
    if (codes.length && codes.every((c) => rateCard.completion[c] === 'complete')) return 'complete';
    if (codes.some((c) => rateCard.completion[c] === 'complete')) return 'partial';
    return 'in-progress';
  };

  // Determine tabs + active selection
  let tabKeys; let parentCodes;
  if (isBlendedMode) {
    const lanesPresent = ['surface', 'air', 'sdd_ndd'].filter((L) => codesInLane(L).length);
    const activeLane = lanesPresent.includes(rateCard.activeLane) ? rateCard.activeLane : (lanesPresent[0] || 'surface');
    tabKeys = lanesPresent;
    parentCodes = codesInLane(activeLane);
  } else {
    let parents = [...parentsRaw];
    if (rateCard.sortByStatus) {
      const order = { 'in-progress': 0, partial: 1, complete: 2 };
      parents = parents.sort((a, b) => (order[parentStatus(a)] ?? 0) - (order[parentStatus(b)] ?? 0));
    }
    tabKeys = parents;
    const ap = activeParent && parents.includes(activeParent) ? activeParent : parents[0];
    parentCodes = ap ? codesUnderParent(ap) : [];
  }

  const curAcc = activeAcc && parentCodes.includes(activeAcc) ? activeAcc : parentCodes[0];
  const accRows = rows.filter((r) => r.account_code === curAcc);
  const svcId = accRows[0]?.service;
  const ar = (svcId && arrangement[svcId]) || { type: 'ZWZ' };
  const arrMeta = ARRANGEMENTS.find((a) => a.id === ar.type) || ARRANGEMENTS[0];

  const activeParentName = (activeParent && tabKeys.includes(activeParent)) ? activeParent : (tabKeys[0] || '');
  const parStatus = isBlendedMode
    ? (parentCodes.length && parentCodes.every((a) => rateCard.completion[a] === 'complete') ? 'complete' : 'in-progress')
    : parentStatus(activeParentName);
  const pKey = isBlendedMode
    ? `parent___blended_${rateCard.activeLane}__`
    : `parent_${activeParentName.replace(/[^a-zA-Z0-9]/g, '_')}`;

  const confirmParent = () => {
    for (const c of parentCodes) {
      const cRows = rows.filter((r) => r.account_code === c);
      const cAr = arrangement[cRows[0]?.service] || {};
      if (cAr.ceiling) {
        const lastMax = Math.max(...cRows.map((r) => +r.max || 0));
        if (lastMax < cAr.ceiling) {
          enqueueSnackbar(`${c}: highest slab (${lastMax} GMS) is below the weight ceiling (${cAr.ceiling} GMS) — extend slabs first.`, { variant: 'warning' });
          return;
        }
      }
    }
    dispatch(markRateCardCodes({ codes: parentCodes, status: 'complete' }));
    dispatch(recordConfirmation(pKey));

    // Auto-advance to the next unconfirmed tab so the analyst doesn't hunt for it.
    if (isBlendedMode) {
      const nextLane = tabKeys.find((L) => L !== rateCard.activeLane
        && !(codesInLane(L).length && codesInLane(L).every((a) => rateCard.completion[a] === 'complete')));
      if (nextLane) { dispatch(setActiveLane(nextLane)); setActiveAcc(null); }
    } else {
      const nextParent = tabKeys.find((p) => p !== activeParentName && parentStatus(p) !== 'complete');
      if (nextParent) { setActiveParent(nextParent); setActiveAcc(null); }
    }
  };
  const reopenParent = () => {
    dispatch(markRateCardCodes({ codes: parentCodes, status: 'in-progress' }));
    dispatch(clearConfirmation(pKey));
  };

  // Overall confirmation progress across all tabs (parent couriers / lanes).
  const laneComplete = (L) => codesInLane(L).length > 0 && codesInLane(L).every((a) => rateCard.completion[a] === 'complete');
  const progressTotal = isBlendedMode ? tabKeys.length : parentsRaw.length;
  const progressDone = isBlendedMode
    ? tabKeys.filter(laneComplete).length
    : parentsRaw.filter((p) => parentStatus(p) === 'complete').length;
  const progressPct = progressTotal ? Math.round((progressDone / progressTotal) * 100) : 0;
  const progressNoun = isBlendedMode ? 'lane' : 'parent courier';

  // Confirm every remaining tab in one go. Runs the SAME weight-ceiling check the
  // per-parent confirm runs; a tab that fails it is left untouched and named in the
  // toast, so one bad courier never silently rides in on a bulk confirm.
  const confirmationKeyFor = (unit) => (isBlendedMode
    ? `parent___blended_${unit}__`
    : `parent_${unit.replace(/[^a-zA-Z0-9]/g, '_')}`);
  const ceilingShortfall = (codes) => codes.find((c) => {
    const cRows = rows.filter((r) => r.account_code === c);
    const cAr = arrangement[cRows[0]?.service] || {};
    if (!cAr.ceiling) return false;
    return Math.max(...cRows.map((r) => +r.max || 0)) < cAr.ceiling;
  });
  const pendingUnits = (isBlendedMode ? tabKeys : parentsRaw)
    .filter((u) => (isBlendedMode ? !laneComplete(u) : parentStatus(u) !== 'complete'));
  const confirmAll = () => {
    const okCodes = [];
    const okKeys = [];
    const blocked = [];
    pendingUnits.forEach((u) => {
      const codes = isBlendedMode ? codesInLane(u) : codesUnderParent(u);
      if (!codes.length) return;
      const bad = ceilingShortfall(codes);
      if (bad) blocked.push(`${isBlendedMode ? laneLabel(u) : u} (${bad})`);
      else { okCodes.push(...codes); okKeys.push(confirmationKeyFor(u)); }
    });
    if (okCodes.length) {
      dispatch(markRateCardCodes({ codes: okCodes, status: 'complete' }));
      okKeys.forEach((k) => dispatch(recordConfirmation(k)));
    }
    if (blocked.length) {
      enqueueSnackbar(
        `Confirmed ${okKeys.length} of ${pendingUnits.length}. Slabs below the weight ceiling — extend them first: ${blocked.join(', ')}`,
        { variant: 'warning' },
      );
    } else {
      enqueueSnackbar(`All ${okKeys.length} ${progressNoun}${okKeys.length === 1 ? '' : 's'} confirmed`, { variant: 'success' });
    }
  };

  const onCell = (r, key, value) => dispatch(updateRateCardRow({ account_code: r.account_code, zone: r.zone, start: r.start, key, value }));

  const applySlab = () => {
    dispatch(setSlabTemplate({ slabTemplate: slabSel, customSlabs }));
    setSlabPickerOpen(false);
    enqueueSnackbar('Slab template updated · rate rows rebuilt', { variant: 'info' });
  };

  // Forward + RVP are ONE section now (Rate Card Modification MoM): a single
  // workbook download/upload carries the RVP sheet, so the Mode-B top tabs are gone.
  return (
    <Box>
      {(
        <>
          <BasePricingUpload />

          {rateCard.rows.length === 0 && (
            <Alert severity="warning" icon={<span>📂</span>} sx={{ mt: 1 }}>
              Download the rate-card workbook above, fill in the price cells per courier column × zone × slab
              (plus the RVP / COD / RTO sheets when present), then upload the file to continue.
            </Alert>
          )}

          {rateCard.rows.length > 0 && (
          <Alert severity="info" icon={<span>🧮</span>} sx={{ mb: 1.25 }}>
            <b>Rate card builder for the ProShip admin panel.</b> Per-courier-account-code rate cards in the 17-column schema.
            <b> Confirmation is at parent-courier level</b> — one click confirms every account code under that parent.
          </Alert>)}

          {rateCard.rows.length > 0 && <>
          <Alert severity={agreement.gst_stance === 'inclusive' ? 'warning' : 'info'} sx={{ mb: 1 }}
            action={<Button size="small" onClick={() => setSlabPickerOpen(true)}>Change slab template</Button>}>
            GST: <b>{agreement.gst_stance === 'inclusive' ? 'Inclusive — rates auto-÷1.18' : 'Exclusive'}</b> · VC: <b>{agreement.vc}</b> · Template: <b>{SLAB_TEMPLATES.find((t) => t.id === rateCard.slabTemplate)?.label}</b>
          </Alert>

          <SlabSummary wizard={wizard} />

          {/* Tabs: parents (courier-wise) or lanes (blended) */}
          <Tabs
            value={isBlendedMode ? (tabKeys.includes(rateCard.activeLane) ? rateCard.activeLane : tabKeys[0]) : (activeParent && tabKeys.includes(activeParent) ? activeParent : tabKeys[0]) || false}
            onChange={(_, v) => { if (isBlendedMode) { dispatch(setActiveLane(v)); setActiveAcc(null); } else { setActiveParent(v); setActiveAcc(null); } }}
            variant="scrollable" scrollButtons="auto"
            sx={{ mt: 1.5, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 } }}
          >
            {tabKeys.map((k) => {
              if (isBlendedMode) {
                const codes = codesInLane(k);
                const done = codes.length && codes.every((a) => rateCard.completion[a] === 'complete');
                return <Tab key={k} value={k} label={<Stack direction="row" spacing={0.75} alignItems="center"><span>{laneIconLabel(k)} Pricing</span><Chip size="small" label={`${codes.length}`} color={done ? 'success' : 'default'} sx={{ height: 16, fontSize: 9 }} /></Stack>} />;
              }
              const st = parentStatus(k);
              const notInSystem = isNotInServiceability(k);
              return (
                <Tab
                  key={k}
                  value={k}
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: st === 'complete' ? 'success.main' : 'grey.400' }} />
                      <span>{k}</span>
                      {notInSystem && (
                        <Chip
                          size="small"
                          label="Not in system"
                          sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}
                        />
                      )}
                      <Chip size="small" label={`${codesUnderParent(k).length}`} sx={{ height: 16, fontSize: 9 }} />
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>

          {/* Alert for couriers missing from serviceabilityGroups */}
          {!isBlendedMode && (() => {
            const missing = tabKeys.filter((k) => isNotInServiceability(k));
            if (!missing.length) return null;
            return (
              <Alert severity="error" sx={{ mt: 1, py: 0.75, fontSize: 12.5 }}>
                <b>{missing.length} courier{missing.length > 1 ? 's' : ''} not registered in serviceability:</b>{' '}
                {missing.map((m) => (
                  <Chip key={m} label={m} size="small" sx={{ ml: 0.5, height: 18, fontSize: 10, fontFamily: 'JetBrains Mono, monospace', bgcolor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }} />
                ))}
                {' '}— prices must be entered manually. Contact platform team to register these couriers in serviceabilityGroups.
              </Alert>
            );
          })()}

          {/* Action bar */}
          <Paper variant="outlined" sx={{ mt: 1, p: 1, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'background.neutral' }}>
            {!isBlendedMode && (
              <FormControlLabel
                control={<Switch size="small" checked={rateCard.sortByStatus} onChange={() => dispatch(toggleSortByStatus())} />}
                label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Sort pending first</Typography>}
              />
            )}
            <Box sx={{ flex: 1 }} />
            {/* Overall progress count */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 230 }}>
              <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: progressDone === progressTotal ? 'success.dark' : 'text.secondary', fontWeight: 600 }}>
                <b>{progressDone}</b> of <b>{progressTotal}</b> {progressNoun}{progressTotal === 1 ? '' : 's'} confirmed
              </Typography>
              <LinearProgress variant="determinate" value={progressPct} sx={{ flex: 1, height: 6, borderRadius: 99, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: progressPct === 100 ? 'success.main' : 'primary.main' } }} />
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, minWidth: 34, textAlign: 'right' }}>{progressPct}%</Typography>
            </Stack>
            {pendingUnits.length > 1 && (
              <Button size="small" variant="contained" color="success" onClick={confirmAll}>
                ✓ Confirm all ({pendingUnits.length})
              </Button>
            )}
            <Chip label={parStatus === 'complete' ? '✓ Confirmed' : '⚠ Not confirmed'} color={parStatus === 'complete' ? 'success' : 'warning'} size="small" variant="outlined" />
            {parStatus === 'complete'
              ? <Button size="small" onClick={reopenParent}>↻ Reopen for edit</Button>
              : <Button size="small" variant="contained" color="success" onClick={confirmParent}>✓ Confirm {isBlendedMode ? laneLabel(rateCard.activeLane) : (activeParent || tabKeys[0])} ({parentCodes.length})</Button>}
          </Paper>

          {/* Account-code sub-strip (courier-wise) or applies-to summary (blended) */}
          {isBlendedMode ? (
            <Alert severity="success" sx={{ mt: 1, py: 0.5 }} icon={<span>🌐</span>}>
              {laneLabel(rateCard.activeLane)} rates apply to {parentCodes.length} courier{parentCodes.length === 1 ? '' : 's'}: {parentCodes.map((c) => <Chip key={c} label={c} size="small" sx={{ mr: 0.5, fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, height: 16 }} />)}
            </Alert>
          ) : (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {parentCodes.map((c) => (
                <Button key={c} size="small" variant={c === curAcc ? 'contained' : 'outlined'} onClick={() => setActiveAcc(c)}
                  sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, py: 0.25 }}>
                  <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', mr: 0.75, bgcolor: rateCard.completion[c] === 'complete' ? 'success.main' : 'grey.400' }} />{c}
                </Button>
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
            <Chip label={isBlendedMode ? `${laneIconLabel(rateCard.activeLane)} Blended Rate Card` : curAcc} color="primary" variant="outlined" size="small" sx={{ fontWeight: 700 }} />
            <Chip label={arrMeta.label} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>click any cell to edit · changes save on blur</Typography>
          </Stack>

          {accRows.length > 0
            ? <RateMatrix rows={accRows} accountCode={curAcc} arrangementType={ar.type} onCell={onCell} />
            : <Alert severity="warning" sx={{ mt: 1 }}>No rows for this selection.</Alert>}

          <Alert severity="info" sx={{ mt: 1.5, bgcolor: 'background.neutral' }} icon={<span>ℹ️</span>}>
            <b>Per-courier RTO & T&C overrides</b> are captured in Stage 3 (Commercials → Preview & Confirm). This stage focuses purely on rates × slabs × zones; T&Cs flow through on commit.
          </Alert>

          <Dialog open={slabPickerOpen} onClose={() => setSlabPickerOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Pick slab template</DialogTitle>
            <DialogContent>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {SLAB_TEMPLATES.map((t) => (
                  <Paper key={t.id} variant="outlined" sx={{ p: 1, borderColor: slabSel === t.id ? 'primary.main' : 'divider', cursor: 'pointer' }} onClick={() => setSlabSel(t.id)}>
                    <Stack direction="row" spacing={1} alignItems="center"><Radio checked={slabSel === t.id} sx={{ p: 0.25 }} /><Typography variant="body2" sx={{ fontWeight: 600 }}>{t.label}</Typography></Stack>
                  </Paper>
                ))}
                <TextField size="small" label="Custom slabs (when 'Custom' selected)" value={customSlabs} onChange={(e) => setCustomSlabs(e.target.value)} placeholder="e.g. 250, 500, 500" helperText="Cumulative GMS: 250,500,500 ⇒ [0–250][250–750][750–1250]" />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={() => setSlabPickerOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={applySlab}>Apply</Button>
            </DialogActions>
          </Dialog>
          </>}
        </>
      )}
    </Box>
  );
}

// Forward-stage gate: every forward (lane-covered) code must be confirmed.
export function validateRateCard(wizard) {
  const rows = wizard.rateCard.rows;
  const accs = [...new Set(rows.map((r) => r.account_code))];
  const isBlended = wizard.rateCardMode === 'blended';
  const laneOfAcc = (acc) => { const r = rows.find((x) => x.account_code === acc); return r ? classifyServiceLane(r.service) : 'surface'; };
  const gateCodes = isBlended ? accs.filter((a) => laneOfAcc(a) !== 'reverse') : accs;
  const pending = gateCodes.filter((a) => wizard.rateCard.completion[a] !== 'complete');
  return pending.length === 0 ? {} : { pending: `${pending.length} courier(s) still pending — confirm each before continuing` };
}
